import {
  fetchSlaDefinitions,
  fetchSlaSettings,
  getOrganizationCalendar,
  saveSlaDefinitions,
  toSlaApiError,
} from "api";
import { getTranslation, getTranslationWithParams } from "common";
import dayjs from "dayjs";
import { useSnackbar, useTranslations } from "hooks";
import type { ExtensionElements } from "model/processModel";
import type { SlaSettingsDto } from "model/slaModel";
import {
  SLA_EDITOR_TEXT_KEYS,
  buildSlaDefinitionsPutRequest,
  emptySlaTargetDraft,
  isSlaDraftListEqual,
  isSlaProcessTarget,
  isSlaTargetDraftValid,
  slaEditorTargetKey,
  slaRevisionFromMeta,
  slaTargetDraftDefaultsFromSettings,
  slaTargetDraftFromDefinition,
  type SlaEditorActorOption,
  type SlaOwnerOption,
  type SlaEditorTarget,
  type SlaTargetDraft,
} from "model/slaEditorModel";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSlaOwnerOptions } from "./useSlaOwnerOptions";

/**
 * Bozza SLA di **un modello** (processo + attivita'), condivisa dalle sezioni
 * SLA del pannello proprieta' del designer (`SlaDesignerSection`).
 *
 * E' la parte di stato del vecchio `SlaModelEditor` (tabella della scheda "Sla"
 * delle impostazioni, rimossa il 2026-10-08): legge `GET .../definitions`,
 * tiene le bozze per target e le riscrive tutte insieme con `PUT
 * .../definitions` (upsert per `target_type`/`target_designerid`, le rimozioni
 * di definizioni salvate partono con `active: false`).
 *
 * Persistenza: il designer chiama `persist(id)` subito dopo un salvataggio
 * riuscito del modello (cosi' i nodi nuovi esistono gia' sul Server e il PUT
 * non risponde `sla_target_node_not_found`), con l'id restituito dalla POST in
 * `/process/insert`. Un modello appena creato non ha definizioni: la revisione
 * per il PUT si legge con una GET al primo `persist`.
 *
 * Definizioni **orfane** (attivita' il cui nodo non e' piu' nel diagramma,
 * `getDesignerIds`): non vengono reinviate e restano invariate sul Server, come
 * nell'editor precedente.
 *
 * Target nuovi (processo o attivita', anche in `/process/insert`): partono dai
 * default dell'organizzazione (`slaTargetDraftDefaultsFromSettings`); le
 * definizioni gia' salvate non vengono toccate.
 *
 * Responsabile: solo amministratori, capi reparto e partecipanti del processo
 * (`useSlaOwnerOptions`); `users` resta la lista completa per dare un nome al
 * responsabile salvato che non e' piu' ammesso.
 *
 * Errori di forma (2026-10-14): durata e unita' di un target nuovo partono
 * vuote e **non** rosse. Diventano rosse solo dopo un salvataggio esplicito
 * tentato con quel target incompleto (`validateForSave` o `persist` esplicito):
 * le chiavi di quei target finiscono in `validationTargetKeys` e
 * `isValidationShown` le espone alle card. Si svuotano a salvataggio riuscito,
 * ricarica o rimozione del target; compilato il campo, l'errore sparisce perche'
 * la validazione non lo produce piu'. Gli autosave non accendono mai il rosso.
 *
 * Il gate e' quello delle impostazioni: senza SLA attivo nell'organizzazione
 * (`settings.enabled`) le sezioni non si rendono e non si legge nulla.
 */
export interface SlaModelDraftOptions {
  idorganization: number | null;
  /** `null` finche' il modello non esiste (`/process/insert` non ancora salvato). */
  idprocessmodel: number | null;
  /** Le rotte SLA delle definizioni sono admin-only: senza admin niente letture. */
  enabled: boolean;
  users: SlaEditorActorOption[];
  roles: SlaEditorActorOption[];
  /** Gruppi (reparti) dell'organizzazione: fonte dei capi reparto. */
  groups: SlaEditorActorOption[];
  /** Assegnazioni delle lane del modello (partecipanti del processo). */
  participants: ExtensionElements | null;
  /** Nome del modello, per il target di processo. */
  processName: string;
  /** `designerid` presenti ora nel diagramma; `null` = non leggibili. */
  getDesignerIds?: () => string[] | null;
}

export interface SlaPersistOptions {
  /**
   * Salvataggio chiesto dall'utente: la bozza non valida e gli errori vengono
   * segnalati. Negli autosave la bozza non valida resta in attesa senza
   * snackbar (gli errori sono gia' sui campi).
   */
  explicit?: boolean;
}

export interface SlaModelDraftValue {
  /** SLA attivo nell'organizzazione e utente admin: le sezioni si rendono. */
  available: boolean;
  settings: SlaSettingsDto | null;
  users: SlaEditorActorOption[];
  roles: SlaEditorActorOption[];
  /** Utenti ammessi come responsabile, con la categoria. */
  ownerOptions: SlaOwnerOption[];
  ownerOptionsLoading: boolean;
  processName: string;
  idorganization: number | null;
  /** Timezone dell'organizzazione per la stima; `null` = non leggibile. */
  timezoneId: string | null;
  loadedRevision: number | null;
  loading: boolean;
  saving: boolean;
  conflict: boolean;
  warnings: string[];
  drafts: SlaTargetDraft[];
  dirty: boolean;
  getDraft: (target: SlaEditorTarget) => SlaTargetDraft | null;
  addTarget: (target: SlaEditorTarget) => void;
  updateDraft: (target: SlaEditorTarget, patch: Partial<SlaTargetDraft>) => void;
  removeTarget: (target: SlaEditorTarget) => void;
  reload: () => void;
  /** true = gli errori di forma del target vanno mostrati (salvataggio tentato). */
  isValidationShown: (target: Pick<SlaEditorTarget, "targetType" | "targetDesignerId">) => boolean;
  /**
   * Controllo prima di un salvataggio esplicito del modello: `false` = bozze
   * SLA incomplete; i campi dei target non validi diventano rossi e parte lo
   * snackbar. Non invia nulla.
   */
  validateForSave: () => boolean;
  /** PUT delle bozze se divergono dal salvato; `true` = nulla da fare o salvato. */
  persist: (idprocessmodel: number, options?: SlaPersistOptions) => Promise<boolean>;
}

const SlaModelDraftContext = createContext<SlaModelDraftValue | null>(null);

export const SlaModelDraftProvider = SlaModelDraftContext.Provider;

/** `null` fuori dal designer: le sezioni SLA non si rendono. */
export const useSlaModelDraftContext = () => useContext(SlaModelDraftContext);

export const useSlaModelDraft = ({
  idorganization,
  idprocessmodel,
  enabled,
  users,
  roles,
  groups,
  participants,
  processName,
  getDesignerIds,
}: SlaModelDraftOptions): SlaModelDraftValue => {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();

  const [settings, setSettings] = useState<SlaSettingsDto | null>(null);
  const [timezoneId, setTimezoneId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<SlaTargetDraft[]>([]);
  const [savedDrafts, setSavedDrafts] = useState<SlaTargetDraft[]>([]);
  /** Definizioni salvate da disattivare al prossimo salvataggio (`active: false`). */
  const [removedDrafts, setRemovedDrafts] = useState<SlaTargetDraft[]>([]);
  const [loadedRevision, setLoadedRevision] = useState<number | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  /** Target con un salvataggio esplicito tentato mentre erano incompleti. */
  const [validationTargetKeys, setValidationTargetKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const available = Boolean(enabled && idorganization && settings?.enabled);

  const groupIds = useMemo(() => groups.map((group) => group.id), [groups]);
  const { options: ownerOptions, loading: ownerOptionsLoading } = useSlaOwnerOptions({
    enabled: available,
    idorganization,
    groupIds,
    participants,
    knownUsers: users,
  });
  /** Default di organizzazione per i target nuovi, letti da `addTarget`. */
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  /** Stato corrente per `persist`, chiamato da closure del designer. */
  const stateRef = useRef({ drafts, savedDrafts, removedDrafts, loadedRevision, available });
  stateRef.current = { drafts, savedDrafts, removedDrafts, loadedRevision, available };
  const getDesignerIdsRef = useRef(getDesignerIds);
  getDesignerIdsRef.current = getDesignerIds;
  const persistingRef = useRef(false);
  /** Bozze dell'ultimo PUT fallito: l'autosave non ripete lo stesso errore. */
  const failedDraftsRef = useRef<SlaTargetDraft[] | null>(null);
  /** Modello a cui appartengono le bozze correnti. */
  const draftModelIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !idorganization) {
      setSettings(null);
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const response = await fetchSlaSettings(idorganization);
        if (isMounted) setSettings(response);
      } catch {
        // Impostazioni non leggibili: le sezioni SLA non si rendono.
        if (isMounted) setSettings(null);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [enabled, idorganization]);

  useEffect(() => {
    if (!available || !idorganization) return;
    let isMounted = true;
    (async () => {
      try {
        const today = dayjs().format("YYYY-MM-DD");
        const calendar = await getOrganizationCalendar(idorganization, today, today);
        if (isMounted) setTimezoneId(calendar?.timezoneId || null);
      } catch {
        // Nessuna timezone dedotta: la stima dichiara lo stato.
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [available, idorganization]);

  const resetDrafts = useCallback((next: SlaTargetDraft[]) => {
    setDrafts(next);
    setSavedDrafts(next);
    setRemovedDrafts([]);
    setWarnings([]);
    setValidationTargetKeys(new Set());
    failedDraftsRef.current = null;
  }, []);

  const load = useCallback(
    async (modelId: number) => {
      if (!idorganization) return;
      setLoading(true);
      setConflict(false);
      try {
        const response = await fetchSlaDefinitions(idorganization, modelId);
        resetDrafts((response.definitions ?? []).map(slaTargetDraftFromDefinition));
        setLoadedRevision(response.meta ? slaRevisionFromMeta(response.meta) : null);
      } catch (error) {
        resetDrafts([]);
        setLoadedRevision(null);
        sendSnack({ message: toSlaApiError(error).message, type: "error" });
      } finally {
        setLoading(false);
      }
    },
    [idorganization, resetDrafts, sendSnack],
  );

  // Lettura delle definizioni del modello in modifica. Il passaggio
  // `null -> id` e' la creazione in `/process/insert`: il modello nuovo non ha
  // definizioni e la bozza gia' scritta dall'utente non va sovrascritta.
  const previousModelIdRef = useRef<number | null>(idprocessmodel);
  useEffect(() => {
    const previous = previousModelIdRef.current;
    previousModelIdRef.current = idprocessmodel;
    if (!available || idprocessmodel === null) return;
    if (draftModelIdRef.current === idprocessmodel) return;
    if (
      previous === null &&
      draftModelIdRef.current === null &&
      stateRef.current.drafts.length > 0
    ) {
      // Creazione appena avvenuta: la bozza passa al modello nuovo.
      draftModelIdRef.current = idprocessmodel;
      return;
    }
    draftModelIdRef.current = idprocessmodel;
    load(idprocessmodel);
  }, [available, idprocessmodel, load]);

  const dirty = useMemo(
    () => removedDrafts.length > 0 || !isSlaDraftListEqual(drafts, savedDrafts),
    [drafts, savedDrafts, removedDrafts],
  );

  const getDraft = useCallback(
    (target: SlaEditorTarget) =>
      drafts.find((draft) => slaEditorTargetKey(draft) === slaEditorTargetKey(target)) ??
      null,
    [drafts],
  );

  const addTarget = useCallback((target: SlaEditorTarget) => {
    const targetKey = slaEditorTargetKey(target);
    setDrafts((current) =>
      current.some((draft) => slaEditorTargetKey(draft) === targetKey)
        ? current
        : [
            ...current,
            emptySlaTargetDraft(
              target,
              slaTargetDraftDefaultsFromSettings(settingsRef.current),
            ),
          ],
    );
    // Riaggiunto dopo una rimozione non salvata: diventa un upsert, non una
    // disattivazione.
    setRemovedDrafts((current) =>
      current.filter((draft) => slaEditorTargetKey(draft) !== targetKey),
    );
  }, []);

  const updateDraft = useCallback(
    (target: SlaEditorTarget, patch: Partial<SlaTargetDraft>) => {
      const targetKey = slaEditorTargetKey(target);
      setDrafts((current) =>
        current.map((draft) =>
          slaEditorTargetKey(draft) === targetKey ? { ...draft, ...patch } : draft,
        ),
      );
    },
    [],
  );

  /**
   * Una definizione gia' salvata chiede conferma e finisce in `removedDrafts`
   * (al salvataggio `active: false`); una riga non salvata sparisce e basta.
   */
  const removeTarget = useCallback(
    (target: SlaEditorTarget) => {
      const targetKey = slaEditorTargetKey(target);
      const saved = stateRef.current.savedDrafts.find(
        (draft) => slaEditorTargetKey(draft) === targetKey,
      );
      if (
        saved &&
        !window.confirm(
          getTranslationWithParams(translations, SLA_EDITOR_TEXT_KEYS.removeConfirm, {
            name: target.name,
          }),
        )
      ) {
        return;
      }
      setDrafts((current) =>
        current.filter((draft) => slaEditorTargetKey(draft) !== targetKey),
      );
      setValidationTargetKeys((current) => {
        if (!current.has(targetKey)) return current;
        const next = new Set(current);
        next.delete(targetKey);
        return next;
      });
      if (saved) {
        setRemovedDrafts((current) => [
          ...current.filter((draft) => slaEditorTargetKey(draft) !== targetKey),
          saved,
        ]);
      }
    },
    [translations],
  );

  const reload = useCallback(() => {
    if (draftModelIdRef.current !== null) load(draftModelIdRef.current);
  }, [load]);

  /**
   * Upsert di tutte le righe tranne le orfane (il Server rifiuterebbe il nodo
   * inesistente); le orfane restano invariate sul Server.
   */
  const pendingUpserts = useCallback((drafts: SlaTargetDraft[]) => {
    const designerIds = getDesignerIdsRef.current?.() ?? null;
    const designerIdSet = designerIds ? new Set(designerIds) : null;
    return drafts.filter(
      (draft) =>
        isSlaProcessTarget(draft.targetType) ||
        designerIdSet === null ||
        (draft.targetDesignerId !== null && designerIdSet.has(draft.targetDesignerId)),
    );
  }, []);

  /** Accende il rosso sui target non validi e avvisa; `true` = tutti validi. */
  const flagInvalidUpserts = useCallback(
    (upserts: SlaTargetDraft[]): boolean => {
      const invalid = upserts.filter((draft) => !isSlaTargetDraftValid(draft));
      if (invalid.length === 0) return true;
      setValidationTargetKeys(new Set(invalid.map(slaEditorTargetKey)));
      sendSnack({
        message: getTranslation(translations, "generic.invalid_data.caption"),
        type: "error",
      });
      return false;
    },
    [sendSnack, translations],
  );

  const validateForSave = useCallback((): boolean => {
    const current = stateRef.current;
    if (!current.available) return true;
    return flagInvalidUpserts(pendingUpserts(current.drafts));
  }, [flagInvalidUpserts, pendingUpserts]);

  const isValidationShown = useCallback(
    (target: Pick<SlaEditorTarget, "targetType" | "targetDesignerId">) =>
      validationTargetKeys.has(slaEditorTargetKey(target)),
    [validationTargetKeys],
  );

  const persist = useCallback(
    async (modelId: number, { explicit = false }: SlaPersistOptions = {}) => {
      const current = stateRef.current;
      if (!current.available || !idorganization || !modelId) return true;
      const isDirty =
        current.removedDrafts.length > 0 ||
        !isSlaDraftListEqual(current.drafts, current.savedDrafts);
      if (!isDirty) return true;
      if (persistingRef.current) return false;
      if (!explicit && failedDraftsRef.current === current.drafts) return false;

      const upserts = pendingUpserts(current.drafts);
      if (!upserts.every(isSlaTargetDraftValid)) {
        // Un PUT con durata/unita' vuote non parte mai. Negli autosave la bozza
        // resta in attesa senza rosso ne' snackbar.
        if (explicit) flagInvalidUpserts(upserts);
        return false;
      }

      persistingRef.current = true;
      setSaving(true);
      const sentDrafts = current.drafts;
      try {
        let revision = draftModelIdRef.current === modelId ? current.loadedRevision : null;
        if (revision === null) {
          // Modello appena creato (o revisione non letta): la revisione del
          // contratto la dice la GET.
          const response = await fetchSlaDefinitions(idorganization, modelId);
          revision = response.meta ? slaRevisionFromMeta(response.meta) : null;
          if (revision === null) return false;
          draftModelIdRef.current = modelId;
          setLoadedRevision(revision);
        }
        const response = await saveSlaDefinitions(
          idorganization,
          modelId,
          buildSlaDefinitionsPutRequest(revision, revision, upserts, current.removedDrafts),
        );
        setSavedDrafts(sentDrafts);
        setRemovedDrafts([]);
        setConflict(false);
        setValidationTargetKeys(new Set());
        failedDraftsRef.current = null;
        setWarnings(Array.isArray(response.meta?.warnings) ? response.meta.warnings : []);
        return true;
      } catch (error) {
        failedDraftsRef.current = sentDrafts;
        const apiError = toSlaApiError(error);
        if (apiError.revisionConflict) {
          // La bozza resta intatta: l'utente decide se ricaricare.
          setConflict(true);
        } else {
          // I 400 del PUT (`sla_target_node_not_found`, ...) possono arrivare
          // come chiave: si passa dal catalogo.
          sendSnack({
            message: getTranslation(translations, apiError.message),
            type: "error",
          });
        }
        return false;
      } finally {
        persistingRef.current = false;
        setSaving(false);
      }
    },
    [idorganization, sendSnack, translations, pendingUpserts, flagInvalidUpserts],
  );

  return useMemo(
    () => ({
      available,
      settings,
      users,
      roles,
      ownerOptions,
      ownerOptionsLoading,
      processName,
      idorganization,
      timezoneId,
      loadedRevision,
      loading,
      saving,
      conflict,
      warnings,
      drafts,
      dirty,
      getDraft,
      addTarget,
      updateDraft,
      removeTarget,
      reload,
      isValidationShown,
      validateForSave,
      persist,
    }),
    [
      available,
      settings,
      users,
      roles,
      ownerOptions,
      ownerOptionsLoading,
      processName,
      idorganization,
      timezoneId,
      loadedRevision,
      loading,
      saving,
      conflict,
      warnings,
      drafts,
      dirty,
      getDraft,
      addTarget,
      updateDraft,
      removeTarget,
      reload,
      isValidationShown,
      validateForSave,
      persist,
    ],
  );
};
